<template>
  <div class="sc-landing">

    <!-- ═══════ NAVBAR ═══════ -->
    <nav class="sc-nav" :class="{ 'sc-nav--scrolled': scrolled }">
      <div class="sc-container sc-nav__inner">
        <router-link class="sc-nav__logo" to="/">
          <img v-if="company && company.logo" :src="imgUrl + 'logo/' + company.logo" :alt="(company && company.name) || 'Structurecity'" />
          <span v-else class="sc-nav__logo-text">Structure<span>city</span></span>
        </router-link>

        <div class="sc-nav__links" :class="{ 'sc-nav__links--open': menuOpen }">
          <router-link to="/">Home</router-link>
          <a href="https://store.structurecity.com/" target="_blank">Marketplace</a>
          <router-link to="/listings">Listings</router-link>
          <router-link to="/blog">Blog</router-link>
          <router-link to="/services">Services</router-link>
          <router-link to="/about">About</router-link>
          <router-link to="/contact">Contact</router-link>
          <div class="sc-nav__mobile-actions">
            <template v-if="isLoggedIn">
              <router-link to="/listing" class="btn-primary">Go to App</router-link>
            </template>
            <template v-else>
              <router-link to="/sign-in" class="btn-ghost">Log in</router-link>
              <router-link to="/register" class="btn-primary">Get started</router-link>
            </template>
          </div>
        </div>

        <!-- Nav search -->
        <div class="sc-nav__search" :class="{ 'sc-nav__search--open': navSearchOpen }" ref="navSearchWrap">
          <transition name="sc-search-expand">
            <input
              v-if="navSearchOpen"
              ref="navSearchInput"
              class="sc-nav__search-input"
              type="search"
              placeholder="Search listings, people, news…"
              v-model="navSearchQuery"
              @keydown.enter="doNavSearch"
              @keydown.esc="closeNavSearch"
            />
          </transition>
          <button
            class="sc-nav__search-btn"
            :class="{ 'sc-nav__search-btn--active': navSearchOpen }"
            @click.stop="toggleNavSearch"
            aria-label="Search"
          >
            <svg v-if="!navSearchOpen" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="pointer-events:none"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="pointer-events:none"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div class="sc-nav__actions">
          <template v-if="isLoggedIn">
            <router-link to="/listing" class="btn-primary">Go to App</router-link>
          </template>
          <template v-else>
            <router-link to="/sign-in" class="btn-ghost">Log in</router-link>
            <router-link to="/register" class="btn-primary">Get started</router-link>
          </template>
        </div>

        <button class="sc-nav__burger" @click="menuOpen = !menuOpen" aria-label="Menu">
          <span :class="{ open: menuOpen }"></span>
          <span :class="{ open: menuOpen }"></span>
          <span :class="{ open: menuOpen }"></span>
        </button>
      </div>
    </nav>

    <!-- ═══════ HERO ═══════ -->
    <section class="sc-hero">
      <div class="sc-hero__dots"></div>
      <div class="sc-container sc-hero__content">
        <span class="sc-hero__badge">Africa's #1 Construction &amp; Real Estate Community</span>
        <h1 class="sc-hero__title">
          Discover Projects,<br>
          <span class="sc-hero__gradient">Properties &amp; Concepts</span>
        </h1>
        <p class="sc-hero__sub">
          Browse thousands of construction projects, real estate listings, and architectural plans.
          Connect with professionals across Africa and beyond.
        </p>

        <div class="sc-search">
          <!-- Main row -->
          <div class="sc-search__row">
            <div class="sc-search__type">
              <select v-model="searchType">
                <option value="">All Types</option>
                <option value="project">Projects</option>
                <option value="property">Properties</option>
                <option value="plan">Concepts</option>
                <option value="user">People</option>
                <option value="company">Companies</option>
              </select>
            </div>
            <input
              class="sc-search__input"
              type="text"
              v-model="searchQuery"
              placeholder="Search listings, people, companies..."
              @keyup.enter="goSearch"
            />
            <button class="sc-search__btn" @click="goSearch">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              Search
            </button>
          </div>
          <!-- Location row -->
          <div class="sc-search__loc-row">
            <svg class="sc-search__loc-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            <select v-model="searchCountry" @change="onCountryChange" class="sc-search__loc-sel">
              <option value="">Country</option>
              <option v-for="c in countries" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
            <span class="sc-search__loc-div"></span>
            <select v-model="searchState" @change="onStateChange" class="sc-search__loc-sel" :disabled="!searchCountry">
              <option value="">State / Region</option>
              <option v-for="s in states" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
            <span class="sc-search__loc-div"></span>
            <select v-model="searchCity" class="sc-search__loc-sel" :disabled="!searchState">
              <option value="">City</option>
              <option v-for="c in cities" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
          </div>
        </div>

        <div class="sc-hero__stats">
          <div class="sc-hero__stat"><strong>5,000+</strong><span>Active Members</span></div>
          <div class="sc-hero__stat-divider"></div>
          <div class="sc-hero__stat"><strong>2,000+</strong><span>Listings</span></div>
          <div class="sc-hero__stat-divider"></div>
          <div class="sc-hero__stat"><strong>20+</strong><span>Countries</span></div>
        </div>

        <div class="sc-hero__cta-row">
          <a href="/listings" class="btn-secondary">Browse Listings</a>
          <router-link to="/register" class="btn-primary">Join for free</router-link>
        </div>
      </div>
    </section>

    <!-- ═══════ FEATURED LISTINGS ═══════ -->
    <section class="sc-section sc-featured">
      <div class="sc-container">
        <div class="sc-section-header">
          <div>
            <h2>Featured Listings</h2>
            <p>Latest projects, properties, and concepts from our community</p>
          </div>
          <div class="sc-section-header__right">
            <div class="filter-tabs">
              <button
                v-for="tab in feedTabs"
                :key="tab.value"
                :class="{ active: activeTab === tab.value }"
                @click="activeTab = tab.value"
              >{{ tab.label }}</button>
            </div>
            <a href="/listings" class="btn-outline">View all</a>
          </div>
        </div>

        <div v-if="loadingFeeds" class="listing-grid">
          <div v-for="n in 6" :key="n" class="listing-card">
            <div class="card-image skeleton" style="aspect-ratio:16/10;"></div>
            <div class="card-body">
              <div class="skeleton" style="height:18px;width:80%;margin-bottom:8px;border-radius:4px;"></div>
              <div class="skeleton" style="height:14px;width:55%;margin-bottom:20px;border-radius:4px;"></div>
              <div style="display:flex;align-items:center;gap:8px;padding-top:12px;border-top:1px solid var(--border-light);">
                <div class="skeleton" style="height:28px;width:28px;border-radius:50%;"></div>
                <div class="skeleton" style="height:12px;width:40%;border-radius:4px;"></div>
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="filteredFeeds.length" class="listing-grid">
          <a
            v-for="item in filteredFeeds"
            :key="item.id"
            :href="getDetailUrl(item)"
            class="listing-card"
          >
            <div class="card-image">
              <img :src="getItemImage(item)" :alt="getItemTitle(item)" @error="onImgError" />
              <span class="card-badge" :class="item.type">{{ formatType(item.type) }}</span>
            </div>
            <div class="card-body">
              <h3>{{ getItemTitle(item) }}</h3>
              <div class="card-meta">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                <span>{{ getItemLocation(item) }}</span>
              </div>
              <div class="card-footer">
                <div class="card-author">
                  <img :src="getAvatarUrl(item.user)" :alt="item.user ? item.user.name : ''" @error="onImgError" />
                  <span>{{ item.user ? item.user.name : '' }}</span>
                </div>
                <div class="card-stats">
                  <span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                    {{ item.likes ? item.likes.length : 0 }}
                  </span>
                  <span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                    {{ item.comments ? item.comments.length : 0 }}
                  </span>
                </div>
              </div>
            </div>
          </a>
        </div>

        <div v-else class="sc-empty">
          <p>No listings found. <a href="#" @click.prevent="loadFeeds">Try refreshing</a></p>
        </div>
      </div>
    </section>

    <!-- ═══════ CATEGORIES ═══════ -->
    <section class="sc-section sc-categories">
      <div class="sc-container">
        <div class="sc-section-header" style="justify-content:center;text-align:center;">
          <div>
            <h2 style="color:#fff;">Browse by Category</h2>
            <p style="color:rgba(255,255,255,0.65);">Find listings in your area of interest</p>
          </div>
        </div>
        <div class="sc-cats-grid">
          <router-link to="/listings?type=project" class="sc-cat-card">
            <div class="sc-cat-icon">🏗️</div><span>Construction Projects</span>
          </router-link>
          <router-link to="/listings?type=property" class="sc-cat-card">
            <div class="sc-cat-icon">🏠</div><span>Real Estate</span>
          </router-link>
          <router-link to="/listings?type=plan" class="sc-cat-card">
            <div class="sc-cat-icon">📐</div><span>Architectural Concepts</span>
          </router-link>
          <router-link to="/services" class="sc-cat-card">
            <div class="sc-cat-icon">🔧</div><span>Professional Services</span>
          </router-link>
          <router-link to="/blog" class="sc-cat-card">
            <div class="sc-cat-icon">📰</div><span>Industry News</span>
          </router-link>
          <a href="https://store.structurecity.com/" class="sc-cat-card">
            <div class="sc-cat-icon">🛒</div><span>Marketplace Store</span>
          </a>
        </div>
      </div>
    </section>

    <!-- ═══════ HOW IT WORKS ═══════ -->
    <section class="sc-section sc-how">
      <div class="sc-container">
        <div class="sc-section-header" style="justify-content:center;text-align:center;">
          <div>
            <h2>How It Works</h2>
            <p>Get started in four simple steps</p>
          </div>
        </div>
        <div class="sc-how-grid">
          <div class="sc-how-step">
            <div class="sc-how-num">01</div>
            <h3>Create Your Profile</h3>
            <p>Sign up for free and showcase your skills, experience, and portfolio to thousands of industry professionals.</p>
          </div>
          <div class="sc-how-step">
            <div class="sc-how-num">02</div>
            <h3>Post or Browse Listings</h3>
            <p>List your construction project, property, or architectural concept — or discover thousands of active listings.</p>
          </div>
          <div class="sc-how-step">
            <div class="sc-how-num">03</div>
            <h3>Connect &amp; Collaborate</h3>
            <p>Message professionals directly, follow projects, and build your network across Africa's construction industry.</p>
          </div>
          <div class="sc-how-step">
            <div class="sc-how-num">04</div>
            <h3>Grow Your Business</h3>
            <p>Gain visibility, attract clients, and close deals — turning your Structurecity presence into real-world opportunities.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════ RECENT BLOG ═══════ -->
    <section class="sc-section sc-blog">
      <div class="sc-container">
        <div class="sc-section-header">
          <div>
            <h2>Recent Blog Posts</h2>
            <p>Industry insights, project spotlights, and expert perspectives</p>
          </div>
          <a href="/blog" class="btn-outline">View all posts</a>
        </div>

        <!-- Skeleton -->
        <div v-if="loadingBlog" class="sc-blog-grid">
          <div v-for="n in 3" :key="n" class="sc-blog-card sc-blog-card--skeleton">
            <div class="skeleton sc-blog-card__thumb"></div>
            <div class="sc-blog-card__body">
              <div class="skeleton" style="height:12px;width:48px;border-radius:20px;margin-bottom:.625rem;"></div>
              <div class="skeleton" style="height:18px;width:92%;border-radius:4px;margin-bottom:.375rem;"></div>
              <div class="skeleton" style="height:18px;width:70%;border-radius:4px;margin-bottom:.875rem;"></div>
              <div class="skeleton" style="height:13px;width:44%;border-radius:4px;"></div>
            </div>
          </div>
        </div>

        <!-- Cards -->
        <div v-else-if="featuredPosts.length" class="sc-blog-grid">
          <a
            v-for="post in featuredPosts.slice(0, 3)"
            :key="post.id"
            :href="'/blog/' + post.slug"
            class="sc-blog-card"
          >
            <div class="sc-blog-card__thumb-wrap">
              <img
                v-if="getBlogImage(post)"
                :src="getBlogImage(post)"
                :alt="post.title"
                class="sc-blog-card__thumb"
                @error="onImgError"
              />
              <div v-else class="sc-blog-card__thumb sc-blog-card__nophoto">
                <svg width="32" height="32" fill="none" stroke="currentColor" stroke-width="1.2" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
              </div>
              <span v-if="post.category" class="sc-blog-card__cat">{{ post.category.name }}</span>
            </div>
            <div class="sc-blog-card__body">
              <h3 class="sc-blog-card__title">{{ post.title.length > 68 ? post.title.substring(0, 68) + '…' : post.title }}</h3>
              <p class="sc-blog-card__date">{{ formatBlogDate(post.created_at) }}</p>
              <span class="sc-blog-card__read">
                Read article
                <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </span>
            </div>
          </a>
        </div>
      </div>
    </section>

    <!-- ═══════ CTA ═══════ -->
    <section class="sc-cta">
      <div class="sc-container sc-cta__inner">
        <h2>Ready to join the community?</h2>
        <p>Sign up free and connect with thousands of construction and real estate professionals across Africa.</p>
        <div class="sc-cta__actions">
          <router-link to="/register" class="btn-primary" style="font-size:1rem;padding:.75rem 2rem;">Create free account</router-link>
          <a href="/listings" class="btn-secondary" style="font-size:1rem;padding:.75rem 2rem;">Browse listings</a>
        </div>
      </div>
    </section>

    <!-- ═══════ FOOTER ═══════ -->
    <footer class="sc-footer">
      <div class="sc-container">
        <div class="sc-footer__top">
          <div class="sc-footer__brand">
            <div class="sc-footer__brand-logo">
              <div v-if="company && company.logo" class="footer-logo-circle">
                <img :src="imgUrl + 'logo/' + company.logo" :alt="(company && company.name) || 'Structurecity'" />
              </div>
              <span class="sc-nav__logo-text" style="font-size:1.5rem;">Structure<span>city</span></span>
            </div>
            <p>Africa's premier construction and real estate social platform. Connect, discover, and grow.</p>
          </div>
          <div class="sc-footer__links">
            <div>
              <h4>Platform</h4>
              <router-link to="/listings">Browse Listings</router-link>
              <router-link to="/services">Services</router-link>
              <router-link to="/blog">Blog</router-link>
              <a href="https://store.structurecity.com/" target="_blank">Marketplace</a>
            </div>
            <div>
              <h4>Community</h4>
              <router-link to="/listing">My Feed</router-link>
              <router-link to="/about">About Us</router-link>
              <router-link to="/contact">Contact</router-link>
            </div>
            <div>
              <h4>Legal</h4>
              <router-link to="/terms-and-conditions">Terms &amp; Conditions</router-link>
              <router-link to="/privacy-policy">Privacy Policy</router-link>
            </div>
          </div>
        </div>
        <div class="sc-footer__bottom">
          <span>&copy; {{ currentYear }} Structurecity. All rights reserved.</span>
          <span v-if="company && company.email">{{ company.email }}</span>
        </div>
        <div class="sc-footer__credit">
          Developed &amp; managed by <a href="https://www.essyp.com" target="_blank" rel="noopener">Essyp</a>
        </div>
      </div>
    </footer>

    <!-- ═══════ LOGIN MODAL ═══════ -->
    <div class="modal fade sc-modal" id="login" tabindex="-1" role="dialog" aria-labelledby="loginTitle">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content sc-modal__content">
          <div class="sc-modal__header">
            <h3 id="loginTitle">Welcome back</h3>
            <button type="button" class="sc-modal__close" data-dismiss="modal" aria-label="Close">&times;</button>
          </div>
          <div class="sc-modal__body">
            <div v-if="loginError" class="sc-alert sc-alert--error">{{ loginError }}</div>
            <form id="login-form" @submit.prevent="submitLogin">
              <div class="sc-field">
                <label>Email address</label>
                <input type="email" name="email" v-model="loginEmail" placeholder="you@example.com" required autocomplete="email" />
              </div>
              <div class="sc-field">
                <label>Password</label>
                <input type="password" name="password" v-model="loginPassword" placeholder="Your password" required autocomplete="current-password" />
              </div>
              <button type="submit" class="btn-primary sc-modal__submit" :disabled="loginLoading">
                <span v-if="loginLoading" class="sc-spinner"></span>
                <span v-else>Log in</span>
              </button>
            </form>
            <div class="sc-modal__footer-links">
              <a href="#" @click.prevent="switchToForgot">Forgot password?</a>
              &nbsp;·&nbsp;
              <a href="#" @click.prevent="switchToSignup">Create account</a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ═══════ SIGNUP MODAL ═══════ -->
    <div class="modal fade sc-modal" id="signup" tabindex="-1" role="dialog" aria-labelledby="signupTitle">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content sc-modal__content">
          <div class="sc-modal__header">
            <h3 id="signupTitle">Join Structurecity</h3>
            <button type="button" class="sc-modal__close" data-dismiss="modal" aria-label="Close">&times;</button>
          </div>
          <div class="sc-modal__body">
            <div v-if="registerError" class="sc-alert sc-alert--error">{{ registerError }}</div>
            <div v-if="registerSuccess" class="sc-alert sc-alert--success">{{ registerSuccess }}</div>
            <form id="register-form" @submit.prevent="submitRegister">
              <div class="sc-field-row">
                <div class="sc-field">
                  <label>First name</label>
                  <input type="text" name="fname" v-model="regFname" placeholder="First name" required />
                </div>
                <div class="sc-field">
                  <label>Last name</label>
                  <input type="text" name="lname" v-model="regLname" placeholder="Last name" required />
                </div>
              </div>
              <div class="sc-field">
                <label>Email address</label>
                <input type="email" name="email" v-model="regEmail" placeholder="you@example.com" required autocomplete="email" />
              </div>
              <div class="sc-field">
                <label>Password</label>
                <input type="password" name="password" v-model="regPassword" placeholder="Minimum 8 characters" required minlength="8" autocomplete="new-password" />
              </div>
              <div class="sc-field">
                <label>Account type</label>
                <select name="type" v-model="regType">
                  <option value="individual">Individual / Professional</option>
                  <option value="company">Company / Business</option>
                </select>
              </div>
              <button type="submit" class="btn-primary sc-modal__submit" :disabled="registerLoading">
                <span v-if="registerLoading" class="sc-spinner"></span>
                <span v-else>Create account</span>
              </button>
            </form>
            <div class="sc-modal__footer-links">
              Already have an account?
              <a href="#" @click.prevent="switchToLogin">Log in</a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ═══════ FORGOT PASSWORD MODAL ═══════ -->
    <div class="modal fade sc-modal" id="forgot" tabindex="-1" role="dialog" aria-labelledby="forgotTitle">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content sc-modal__content">
          <div class="sc-modal__header">
            <h3 id="forgotTitle">Reset your password</h3>
            <button type="button" class="sc-modal__close" data-dismiss="modal" aria-label="Close">&times;</button>
          </div>
          <div class="sc-modal__body">
            <div v-if="forgotError" class="sc-alert sc-alert--error">{{ forgotError }}</div>
            <div v-if="forgotSuccess" class="sc-alert sc-alert--success">{{ forgotSuccess }}</div>
            <p class="sc-hint">Enter your email and we'll send you a reset link.</p>
            <form @submit.prevent="submitForgot">
              <div class="sc-field">
                <label>Email address</label>
                <input type="email" v-model="forgotEmail" placeholder="you@example.com" required autocomplete="email" />
              </div>
              <button type="submit" class="btn-primary sc-modal__submit" :disabled="forgotLoading">
                <span v-if="forgotLoading" class="sc-spinner"></span>
                <span v-else>Send reset link</span>
              </button>
            </form>
            <div class="sc-modal__footer-links">
              <a href="#" @click.prevent="switchToLogin">Back to login</a>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { useSEO, SITE_ORIGIN } from '@/utils/seo';

export default {
  name: 'Index',
  data() {
    return {
      imgUrl:  process.env.VUE_APP_IMAGE_URL || '',
      apiUrl:  process.env.VUE_APP_API_URL   || '/api/',
      scrolled: false,
      menuOpen: false,
      navSearchOpen: false,
      navSearchQuery: '',
      company: null,
      currentYear: new Date().getFullYear(),

      featuredPosts: [],
      loadingBlog: true,

      feeds: [],
      loadingFeeds: true,
      activeTab: '',
      feedTabs: [
        { label: 'All',        value: '' },
        { label: 'Projects',   value: 'project' },
        { label: 'Properties', value: 'property' },
        { label: 'Concepts',   value: 'plan' },
      ],

      searchType:    '',
      searchQuery:   '',
      searchCountry: '',
      searchState:   '',
      searchCity:    '',
      countries:     [],
      states:        [],
      cities:        [],

      // login
      loginEmail: '', loginPassword: '', loginLoading: false, loginError: '',

      // register
      regFname: '', regLname: '', regEmail: '', regPassword: '',
      regType: 'individual', registerLoading: false,
      registerError: '', registerSuccess: '',

      // forgot
      forgotEmail: '', forgotLoading: false, forgotError: '', forgotSuccess: '',
    };
  },

  computed: {
    isLoggedIn() { return !!localStorage.getItem('user'); },
    filteredFeeds() {
      const tab = this.activeTab;
      const items = tab ? this.feeds.filter(f => f.type === tab || (tab === 'property' && f.type === 'post')) : this.feeds;
      return items.slice(0, 9);
    },
  },

  mounted() {
    useSEO({
      title: "Africa's #1 Construction & Real Estate Platform",
      description: "Browse verified construction projects, properties for sale or rent, and architectural plans. Connect with professionals and grow your business across Africa on Structurecity.",
      url: SITE_ORIGIN + '/',
    });
    window.addEventListener('scroll', this.onScroll, { passive: true });
    document.addEventListener('click', this.onDocClick);
    this.loadFeeds();
    this.loadCompany();
    this.loadFeaturedPosts();
    this.fetchCountries();
  },

  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('click', this.onDocClick);
  },

  methods: {
    onScroll() {
      this.scrolled = window.scrollY > 60;
    },

    onDocClick(e) {
      if (this.$refs.navSearchWrap && !this.$refs.navSearchWrap.contains(e.target)) {
        this.closeNavSearch();
      }
    },
    toggleNavSearch() {
      if (this.navSearchOpen) {
        this.doNavSearch();
      } else {
        this.navSearchOpen = true;
        this.$nextTick(() => {
          if (this.$refs.navSearchInput) this.$refs.navSearchInput.focus();
        });
      }
    },
    closeNavSearch() {
      this.navSearchOpen = false;
      this.navSearchQuery = '';
    },
    doNavSearch() {
      const q = this.navSearchQuery.trim();
      if (!q) { this.closeNavSearch(); return; }
      this.$router.push({ path: '/search', query: { q } });
      this.closeNavSearch();
      this.menuOpen = false;
    },

    loadFeeds() {
      this.loadingFeeds = true;
      this.axios.get(this.apiUrl + 'feeds')
        .then(res => {
          if (res.status === 200) {
            const payload = res.data;
            this.feeds = Array.isArray(payload) ? payload : (payload && payload.data ? payload.data : []);
          }
        })
        .catch(() => {})
        .finally(() => { this.loadingFeeds = false; });
    },

    loadFeaturedPosts() {
      this.loadingBlog = true;
      this.axios.get(this.apiUrl + 'blog/all')
        .then(res => {
          if (res.status === 200) {
            const posts = res.data && res.data.data ? res.data.data : (Array.isArray(res.data) ? res.data : []);
            this.featuredPosts = posts.slice(0, 3);
          }
        })
        .catch(() => {})
        .finally(() => { this.loadingBlog = false; });
    },

    getBlogImage(post) {
      if (post.images && post.images.length) return post.images[0].path;
      if (post.image) return post.image;
      return null;
    },

    formatBlogDate(d) {
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const dt = new Date(d);
      return months[dt.getMonth()] + ' ' + dt.getDate() + ', ' + dt.getFullYear();
    },

    loadCompany() {
      this.axios.get(this.apiUrl + 'company')
        .then(res => {
          if (res && res.data) this.company = res.data;
          else if (res && res.status === 200) this.company = res.data;
        })
        .catch(() => {});
    },

    getItemImage(item) {
      const fallback = 'https://placehold.co/600x380/e5e7eb/9ca3af?text=No+Image';
      const tryImages = (obj) => {
        if (!obj || !obj.images || !obj.images.length) return null;
        const img = obj.images[0];
        return img.path || (img.image ? this.imgUrl + img.image : null);
      };
      return tryImages(item.project) || tryImages(item.post) || tryImages(item.plan) || fallback;
    },

    getItemTitle(item) {
      return (item.project && item.project.name) ||
             (item.post && item.post.name) ||
             (item.plan && item.plan.name) || 'Untitled';
    },

    getItemLocation(item) {
      const src = item.project || item.post || item.plan;
      if (!src) return 'Location N/A';
      const city    = src.city    && src.city.name;
      const state   = src.state   && src.state.name;
      const country = src.country && src.country.name;
      return [city || state, country].filter(Boolean).join(', ') || 'Location N/A';
    },

    getDetailUrl(item) {
      if (item.project) return '/listing/project/'  + item.project.slug;
      if (item.post)    return '/listing/property/' + item.post.slug;
      if (item.plan)    return '/listing/plan/'     + item.plan.slug;
      return '/listings';
    },

    getAvatarUrl(user) {
      if (!user) return 'https://placehold.co/40x40/e5e7eb/9ca3af?text=?';
      if (user.type === 'company' && user.company && user.company.logo)
        return this.imgUrl + user.company.logo;
      if (user.avatar) return this.$avatarSrc(user.avatar, 'users');
      return 'https://placehold.co/40x40/dbeafe/1d4ed8?text=' + encodeURIComponent((user.name || '?')[0].toUpperCase());
    },

    formatType(type) {
      return { project: 'Project', property: 'Property', post: 'Property', plan: 'Plan' }[type] || type;
    },

    onImgError(e) {
      e.target.src = 'https://placehold.co/600x380/e5e7eb/9ca3af?text=No+Image';
    },

    goSearch() {
      const params = new URLSearchParams();
      if (this.searchQuery.trim()) params.set('q',          this.searchQuery.trim());
      if (this.searchType)         params.set('type',       this.searchType);
      if (this.searchCountry)      params.set('country_id', this.searchCountry);
      if (this.searchState)        params.set('state_id',   this.searchState);
      if (this.searchCity)         params.set('city_id',    this.searchCity);
      const qs = params.toString();
      window.location.href = '/search' + (qs ? '?' + qs : '');
    },

    fetchCountries() {
      this.axios.get(this.apiUrl + 'country')
        .then(res => { if (res.status === 200) this.countries = res.data || []; })
        .catch(() => {});
    },

    onCountryChange() {
      this.searchState = '';
      this.searchCity  = '';
      this.states      = [];
      this.cities      = [];
      if (!this.searchCountry) return;
      this.axios.get(this.apiUrl + 'state/' + this.searchCountry)
        .then(res => { if (res.status === 200) this.states = res.data || []; })
        .catch(() => {});
    },

    onStateChange() {
      this.searchCity = '';
      this.cities     = [];
      if (!this.searchState) return;
      this.axios.get(this.apiUrl + 'city/' + this.searchState)
        .then(res => { if (res.status === 200) this.cities = res.data || []; })
        .catch(() => {});
    },

    switchToLogin()  { window.$('#signup,#forgot').modal('hide'); window.$('#login').modal('show'); },
    switchToSignup() { window.$('#login,#forgot').modal('hide');  window.$('#signup').modal('show'); },
    switchToForgot() { window.$('#login,#signup').modal('hide');  window.$('#forgot').modal('show'); },

    submitLogin() {
      this.loginError   = '';
      this.loginLoading = true;
      const data = new FormData(document.getElementById('login-form'));
      this.axios.post(this.apiUrl + 'auth/login', data)
        .then(res => {
          if (res.status === 200) {
            localStorage.setItem('user', res.data.data.id);
            localStorage.setItem('slug', res.data.data.slug);
            localStorage.setItem('token', res.data.token);
            window.$('#login').modal('hide');
            this.$router.push({ name: 'Home' });
          } else {
            this.loginError = res.message || 'Login failed. Please check your credentials.';
          }
        })
        .catch(() => { this.loginError = 'Login failed. Please try again.'; })
        .finally(() => { this.loginLoading = false; });
    },

    submitRegister() {
      this.registerError   = '';
      this.registerSuccess = '';
      this.registerLoading = true;
      const data = new FormData(document.getElementById('register-form'));
      this.axios.post(this.apiUrl + 'auth/register', data)
        .then(res => {
          if (res.status === 200) {
            this.registerSuccess = 'Account created! Please check your email to verify your account.';
            this.regFname = this.regLname = this.regEmail = this.regPassword = '';
          } else {
            this.registerError = res.message || 'Registration failed. Please try again.';
          }
        })
        .catch(() => { this.registerError = 'Registration failed. Please try again.'; })
        .finally(() => { this.registerLoading = false; });
    },

    submitForgot() {
      this.forgotError   = '';
      this.forgotSuccess = '';
      this.forgotLoading = true;
      this.axios.post(this.apiUrl + 'auth/forgot-password', { email: this.forgotEmail })
        .then(res => {
          if (res.status === 200) {
            this.forgotSuccess = 'Reset link sent! Check your email inbox.';
            this.forgotEmail   = '';
          } else {
            this.forgotError = res.message || 'Could not send reset email. Please try again.';
          }
        })
        .catch(() => { this.forgotError = 'Could not send reset email. Please try again.'; })
        .finally(() => { this.forgotLoading = false; });
    },
  },
};
</script>

<style scoped>
/* ── Nav ────────────────────────────────────────────── */
.sc-nav {
  position: fixed; top: 0; left: 0; right: 0;
  z-index: 1000;
  background: rgba(255,255,255,0.92);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border-bottom: 1px solid transparent;
  transition: border-color .2s, box-shadow .2s;
}
.sc-nav--scrolled { border-bottom-color: var(--border); box-shadow: var(--shadow-sm); }
.sc-nav__inner { display: flex; align-items: center; gap: 1.5rem; height: 68px; }
.sc-nav__logo { display: flex; align-items: center; text-decoration: none; }
.sc-nav__logo img { height: 36px; object-fit: contain; }
.sc-nav__logo-text {
  font-size: 1.375rem; font-weight: 800; color: var(--primary);
  text-decoration: none; letter-spacing: -.03em;
}
.sc-nav__logo-text span { color: var(--accent); }
.sc-nav__links { display: flex; align-items: center; gap: .125rem; margin-left: auto; }
.sc-nav__links a {
  padding: .375rem .875rem; font-size: .9375rem; font-weight: 500;
  color: var(--text-muted); text-decoration: none; border-radius: var(--radius-full);
  transition: color .15s, background .15s;
}
.sc-nav__links a:hover { color: var(--text); background: var(--surface-3); }
.sc-nav__actions { display: flex; align-items: center; gap: .5rem; }

/* Nav search */
.sc-nav__search { display: flex; align-items: center; gap: .25rem; }
.sc-nav__search-input {
  padding: .375rem .875rem;
  border: 1.5px solid var(--border);
  border-radius: var(--radius-full, 99px);
  font-size: .875rem; outline: none;
  background: var(--surface-2, #f8fafc);
  color: var(--text); font-family: inherit;
  width: 220px;
  transition: border-color .15s;
}
.sc-nav__search-input:focus { border-color: var(--primary); background: #fff; }
.sc-nav__search-input::placeholder { color: var(--text-muted); }
.sc-nav__search-btn {
  width: 36px; height: 36px; border-radius: 50%;
  border: none; background: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  color: var(--text-muted, #6b7280);
  transition: background .15s, color .15s;
  flex-shrink: 0;
}
.sc-nav__search-btn:hover,
.sc-nav__search-btn--active { background: var(--surface-3, #f1f5f9); color: var(--primary); }
.sc-search-expand-enter-active, .sc-search-expand-leave-active { transition: width .25s ease, opacity .2s; }
.sc-search-expand-enter-from, .sc-search-expand-leave-to { width: 0; opacity: 0; }

.sc-nav__burger {
  display: none; flex-direction: column; gap: 5px;
  padding: .5rem; background: none; border: none; cursor: pointer; margin-left: auto;
}
.sc-nav__burger span {
  display: block; width: 22px; height: 2px; background: var(--text);
  border-radius: 2px; transition: transform .2s, opacity .2s;
}
.sc-nav__burger span.open:nth-child(1) { transform: translateY(7px) rotate(45deg); }
.sc-nav__burger span.open:nth-child(2) { opacity: 0; }
.sc-nav__burger span.open:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

/* ── Hero ───────────────────────────────────────────── */
.sc-hero {
  position: relative; min-height: 100vh;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #0d1f36 0%, var(--primary) 55%, #1a4a7a 100%);
  padding: 9rem 1.5rem 7rem; overflow: hidden;
}
.sc-hero__dots {
  position: absolute; inset: 0; pointer-events: none;
  background-image: radial-gradient(circle, rgba(255,255,255,.06) 1px, transparent 1px);
  background-size: 40px 40px;
}
.sc-hero::after {
  content: ''; position: absolute; bottom: -1px; left: 0; right: 0;
  height: 80px; background: var(--surface-2);
  clip-path: ellipse(60% 100% at 50% 100%);
}
.sc-hero__content {
  position: relative; z-index: 2; text-align: center;
  max-width: 780px; margin: 0 auto;
  display: flex; flex-direction: column; align-items: center; gap: 1.375rem;
}
.sc-hero__badge {
  display: inline-block; padding: .375rem 1rem;
  background: rgba(245,158,11,.15); border: 1px solid rgba(245,158,11,.4);
  border-radius: var(--radius-full); color: var(--accent);
  font-size: .8125rem; font-weight: 600; letter-spacing: .04em; text-transform: uppercase;
}
.sc-hero__title {
  font-size: clamp(2.25rem, 5vw, 3.75rem); font-weight: 800;
  color: #fff; line-height: 1.1; letter-spacing: -.03em; margin: 0;
}
.sc-hero__gradient {
  background: linear-gradient(90deg, var(--accent), #fbbf24);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
}
.sc-hero__sub {
  font-size: 1.0625rem; color: rgba(255,255,255,.72);
  max-width: 580px; line-height: 1.65; margin: 0;
}

/* ── Search ─────────────────────────────────────────── */
.sc-search {
  display: flex; flex-direction: column; align-items: stretch; background: #fff;
  border-radius: 16px; box-shadow: var(--shadow-xl);
  overflow: hidden; width: 100%; max-width: 680px;
}
.sc-search__row { display: flex; align-items: stretch; }
.sc-search__type { display: flex; align-items: stretch; }
.sc-search__type select {
  height: 100%; padding: 0 1.25rem; border: none; border-right: 1px solid var(--border);
  background: transparent; font-family: var(--font); font-size: .9rem; font-weight: 500;
  color: var(--text); outline: none; cursor: pointer;
  text-align: center; text-align-last: center;
}
.sc-search__input {
  flex: 1; padding: 1rem 1.25rem; border: none;
  font-family: var(--font); font-size: .9375rem; color: var(--text);
  outline: none; min-width: 0;
}
.sc-search__btn {
  display: flex; align-items: center; gap: .5rem;
  padding: 0 1.5rem; background: var(--accent); color: var(--primary-dark);
  font-family: var(--font); font-size: .9375rem; font-weight: 700;
  border: none; cursor: pointer; transition: background .2s; white-space: nowrap;
}
.sc-search__btn:hover { background: var(--accent-dark); }
.sc-search__loc-row {
  display: flex; align-items: center; gap: 0;
  padding: .375rem 1rem; border-top: 1px solid var(--border);
  background: #fafafa;
}
.sc-search__loc-icon { color: var(--text-muted); flex-shrink: 0; margin-right: .5rem; }
.sc-search__loc-sel {
  flex: 1; border: none; background: transparent; outline: none; cursor: pointer;
  font-family: var(--font); font-size: .8125rem; color: var(--text);
  padding: .375rem .25rem; min-width: 0;
}
.sc-search__loc-sel:disabled { color: var(--text-muted); cursor: not-allowed; }
.sc-search__loc-div { width: 1px; height: 16px; background: var(--border); flex-shrink: 0; margin: 0 .25rem; }

/* ── Hero stats ─────────────────────────────────────── */
.sc-hero__stats { display: flex; align-items: center; gap: 2rem; }
.sc-hero__stat { text-align: center; }
.sc-hero__stat strong { display: block; font-size: 1.625rem; font-weight: 800; color: #fff; letter-spacing: -.02em; }
.sc-hero__stat span { font-size: .8125rem; color: rgba(255,255,255,.55); font-weight: 500; }
.sc-hero__stat-divider { width: 1px; height: 40px; background: rgba(255,255,255,.15); }
.sc-hero__cta-row { position: relative; z-index: 2; display: flex; gap: 1rem; }

/* ── Featured ───────────────────────────────────────── */
.sc-featured { background: var(--surface-2); }
.sc-section-header__right { display: flex; align-items: center; gap: 1rem; flex-wrap: wrap; }
.sc-empty { text-align: center; padding: 3rem; color: var(--text-muted); }
.sc-empty a { color: var(--primary); font-weight: 600; }

/* ── Categories ─────────────────────────────────────── */
.sc-categories { background: var(--primary); }
.sc-cats-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 1rem; }
.sc-cat-card {
  display: flex; flex-direction: column; align-items: center; gap: .75rem;
  padding: 1.75rem 1rem;
  background: rgba(255,255,255,.08); border: 1px solid rgba(255,255,255,.12);
  border-radius: var(--radius-lg); text-decoration: none;
  color: rgba(255,255,255,.85); font-weight: 600; font-size: .9rem;
  transition: background .2s, transform .2s; text-align: center;
}
.sc-cat-card:hover { background: rgba(255,255,255,.15); transform: translateY(-2px); color: #fff; text-decoration: none; }
.sc-cat-icon { font-size: 2rem; line-height: 1; }

/* ── How it works ───────────────────────────────────── */
.sc-how { background: var(--surface); }
.sc-how-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 2rem; }
.sc-how-step { text-align: center; padding: 2rem 1.5rem; }
.sc-how-num {
  font-size: 3rem; font-weight: 900; color: var(--accent); opacity: .3;
  line-height: 1; margin-bottom: 1rem; letter-spacing: -.04em;
}
.sc-how-step h3 { font-size: 1.1875rem; font-weight: 700; margin: 0 0 .625rem; color: var(--text); }
.sc-how-step p { color: var(--text-muted); font-size: .9375rem; line-height: 1.65; margin: 0; }

/* ── CTA ────────────────────────────────────────────── */
.sc-cta { background: linear-gradient(135deg, #0d1f36, var(--primary)); padding: 5rem 0; }
.sc-cta__inner { text-align: center; max-width: 640px; margin: 0 auto; }
.sc-cta__inner h2 {
  font-size: 2.25rem; font-weight: 800; color: #fff; margin: 0 0 1rem; letter-spacing: -.03em;
}
.sc-cta__inner p { color: rgba(255,255,255,.7); font-size: 1.0625rem; line-height: 1.65; margin: 0 0 2rem; }
.sc-cta__actions { display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; }

/* ── Footer ─────────────────────────────────────────── */
.sc-footer { background: #0d1f36; color: rgba(255,255,255,.65); padding: 4rem 0 2rem; }
.sc-footer__top {
  display: grid; grid-template-columns: 1.5fr 2fr; gap: 3rem;
  padding-bottom: 3rem; border-bottom: 1px solid rgba(255,255,255,.08);
}
.sc-footer__brand-logo { display: flex; align-items: center; gap: .75rem; }
.footer-logo-circle {
  width: 54px; height: 54px; border-radius: 50%;
  background: #fff; display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; padding: 6px;
  box-shadow: 0 2px 8px rgba(0,0,0,.25);
}
.footer-logo-circle img { width: 100%; height: 100%; object-fit: contain; }
.sc-footer__brand p { margin: 1rem 0 0; font-size: .9rem; line-height: 1.65; max-width: 280px; }
.sc-footer__links { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2rem; }
.sc-footer__links h4 {
  color: #fff; font-size: .875rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: .06em; margin: 0 0 1rem;
}
.sc-footer__links a {
  display: block; color: rgba(255,255,255,.5); text-decoration: none;
  font-size: .9rem; margin-bottom: .625rem; transition: color .15s;
}
.sc-footer__links a:hover { color: #fff; }
.sc-footer__bottom {
  display: flex; justify-content: space-between; flex-wrap: wrap; gap: .5rem;
  padding-top: 2rem; font-size: .8125rem; color: rgba(255,255,255,.3);
}
.sc-footer__credit {
  text-align: center; padding-top: .75rem;
  font-size: .75rem; color: rgba(255,255,255,.25);
}
.sc-footer__credit a { color: rgba(255,255,255,.45); text-decoration: none; }
.sc-footer__credit a:hover { color: #fff; }

/* ── Modals ─────────────────────────────────────────── */
.sc-modal .modal-dialog { max-width: 440px; }
.sc-modal__content {
  border: none !important; border-radius: var(--radius-lg) !important;
  box-shadow: var(--shadow-xl); overflow: hidden;
}
.sc-modal__header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 1.75rem 1.75rem 0;
}
.sc-modal__header h3 {
  font-size: 1.375rem; font-weight: 700; color: var(--text); margin: 0; letter-spacing: -.02em;
}
.sc-modal__close {
  background: none; border: none; font-size: 1.5rem;
  color: var(--text-muted); cursor: pointer; line-height: 1; padding: .25rem; transition: color .15s;
}
.sc-modal__close:hover { color: var(--text); }
.sc-modal__body { padding: 1.5rem 1.75rem 1.75rem; }
.sc-modal__submit {
  width: 100%; justify-content: center; padding: .75rem;
  font-size: 1rem; border-radius: var(--radius-sm); margin-top: .25rem;
}
.sc-modal__footer-links {
  text-align: center; margin-top: 1.25rem; font-size: .875rem; color: var(--text-muted);
}
.sc-modal__footer-links a { color: var(--primary); font-weight: 600; text-decoration: none; }
.sc-modal__footer-links a:hover { text-decoration: underline; }
.sc-hint { color: var(--text-muted); font-size: .9rem; margin: 0 0 1.25rem; }

/* ── Form fields ────────────────────────────────────── */
.sc-field { margin-bottom: 1rem; }
.sc-field label { display: block; font-size: .875rem; font-weight: 500; color: var(--text); margin-bottom: .375rem; }
.sc-field input, .sc-field select {
  width: 100%; padding: .625rem .875rem;
  border: 1.5px solid var(--border); border-radius: var(--radius-sm);
  font-family: var(--font); font-size: .9375rem; color: var(--text);
  background: var(--surface); outline: none; -webkit-appearance: none;
  transition: border-color .15s, box-shadow .15s;
}
.sc-field input:focus, .sc-field select:focus {
  border-color: var(--primary); box-shadow: 0 0 0 3px rgba(30,58,95,.1);
}
.sc-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: .75rem; }

/* ── Alerts ─────────────────────────────────────────── */
.sc-alert { padding: .75rem 1rem; border-radius: var(--radius-sm); font-size: .875rem; margin-bottom: 1rem; }
.sc-alert--error   { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
.sc-alert--success { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; }

/* ── Spinner ────────────────────────────────────────── */
.sc-spinner {
  display: inline-block; width: 18px; height: 18px;
  border: 2px solid rgba(0,0,0,.15); border-top-color: currentColor;
  border-radius: 50%; animation: spin .7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Recent Blog ─────────────────────────────────────── */
.sc-blog { background: var(--surface-2); }
.sc-blog-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}
.sc-blog-card {
  background: var(--surface);
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-light);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  display: flex; flex-direction: column;
  text-decoration: none; color: inherit;
  transition: transform .2s, box-shadow .2s;
}
.sc-blog-card:hover { transform: translateY(-3px); box-shadow: var(--shadow-lg); text-decoration: none; color: inherit; }
.sc-blog-card__thumb-wrap { position: relative; flex-shrink: 0; overflow: hidden; }
.sc-blog-card__thumb {
  width: 100%; height: 200px; object-fit: cover; display: block;
  transition: transform .35s ease;
}
.sc-blog-card:hover .sc-blog-card__thumb { transform: scale(1.04); }
.sc-blog-card__nophoto {
  height: 200px; display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #f0f4f8, #e2eaf4); color: #94a3b8;
}
.sc-blog-card__cat {
  position: absolute; top: .75rem; left: .75rem;
  background: rgba(30,58,95,.85); backdrop-filter: blur(6px);
  color: #fff; font-size: .7rem; font-weight: 700; letter-spacing: .04em;
  text-transform: uppercase; padding: .25rem .625rem;
  border-radius: var(--radius-full);
}
.sc-blog-card__body { padding: 1.25rem; display: flex; flex-direction: column; flex: 1; gap: .5rem; }
.sc-blog-card__title {
  font-size: .9375rem; font-weight: 700; color: var(--text);
  line-height: 1.45; margin: 0;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.sc-blog-card__date { font-size: .8rem; color: var(--text-muted); margin: 0; }
.sc-blog-card__read {
  display: inline-flex; align-items: center; gap: .3rem;
  font-size: .8125rem; font-weight: 700; color: var(--primary);
  margin-top: auto; padding-top: .625rem;
  border-top: 1px solid var(--border-light);
  transition: gap .15s;
}
.sc-blog-card:hover .sc-blog-card__read { gap: .5rem; }
.sc-blog-card--skeleton { pointer-events: none; }

/* ── Responsive ─────────────────────────────────────── */
.sc-nav__mobile-actions { display: none; }
@media (max-width: 900px) {
  .sc-nav__links, .sc-nav__actions { display: none; }
  .sc-nav__search-input { width: min(170px, calc(100vw - 200px)); }
  .sc-nav__burger { display: flex; }
  .sc-nav__links--open {
    display: flex; flex-direction: column; align-items: flex-start;
    position: fixed; top: 68px; left: 0; right: 0;
    background: var(--surface); padding: 1rem 1.5rem 1.5rem;
    box-shadow: var(--shadow-lg); border-bottom: 1px solid var(--border); gap: .25rem;
    z-index: 999;
  }
  .sc-nav__mobile-actions {
    display: flex; gap: .5rem; padding-top: .75rem; margin-top: .25rem;
    border-top: 1px solid var(--border); width: 100%;
  }
}
@media (max-width: 768px) {
  .sc-hero { padding: 8rem 1.25rem 6rem; }
  .sc-hero__stats { gap: 1.25rem; }
  .sc-search { border-radius: 12px; }
  .sc-search__row { flex-direction: column; }
  .sc-search__type select { padding: .75rem 1rem; border-right: none; border-bottom: 1px solid var(--border); }
  .sc-search__btn { padding: .875rem; justify-content: center; }
  .sc-search__loc-row { flex-wrap: wrap; gap: .25rem; padding: .5rem .75rem; }
  .sc-search__loc-sel { min-width: calc(50% - 1rem); }
  .sc-search__loc-div { display: none; }
  .sc-hero__cta-row { flex-direction: column; align-items: center; }
  .sc-footer__top { grid-template-columns: 1fr; }
  .sc-footer__links { grid-template-columns: 1fr 1fr; }
  .sc-field-row { grid-template-columns: 1fr; }
  .sc-section-header__right { flex-direction: column; align-items: flex-start; }
  .sc-blog-grid { grid-template-columns: 1fr; }
}
</style>
